/**
 * EmployeeEnrollmentPage
 *
 * Two-view flow:
 *   1. Unenrolled list   — search, dept/desig filters, infinite scroll
 *   2. Registration flow — input → camera → preview → enrolling → success
 *
 * Role-gated to HRMS_ADMIN.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../App";
import AddonGate from "../../../../components/AddonGate";
import {
  listUnenrolledEmployees,
  checkEnrollmentStatusById,
  enrollFaceEnroll,
  enrollFaceAdditionalEnroll,
  listDepartmentsForFilter,
  listDesignationsForFilter,
} from "../../../../config/Services";
import "./EmployeeEnrollmentPage.css";

/* ─── Constants ─────────────────────────────────────────────── */
const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;
const BRIGHTNESS_MIN = 0.22;
const BRIGHTNESS_MAX = 0.88;
const MIN_B64_LEN = 20_000; // ≈ 15 KB minimum — just rejects empty/corrupt frames; server validates quality
const BURST_COUNT = 3;
const BURST_INTERVAL_MS = 600;

/* ─── Inline SVG icons ──────────────────────────────────────── */
const BackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);
const FilterIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const CameraIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#16a34a"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const PersonIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const XIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

/* ─── Quality helpers ───────────────────────────────────────── */
/** Calculate average luminance from a canvas that already has the frame drawn */
function estimateBrightness(canvas) {
  try {
    const ctx = canvas.getContext("2d");
    const { width: w, height: h } = canvas;
    const data = ctx.getImageData(0, 0, w, h).data;
    let sum = 0,
      count = 0;
    for (let i = 0; i < data.length; i += 32) {
      // sample 1 in 8 pixels
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      count++;
    }
    return count ? sum / count / 255 : 0.5;
  } catch (_) {
    return 0.5;
  }
}

/* ─── Filter sidebar ────────────────────────────────────────── */
function FilterSidebar({
  visible,
  depts,
  desigs,
  loadingMeta,
  draftDeptId,
  draftDesigId,
  onChangeDept,
  onChangeDesig,
  onApply,
  onClear,
  onClose,
}) {
  /* Lock body scroll while open */
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      <div className="eep-filter-backdrop" onClick={onClose} />
      <div className="eep-filter-sidebar" role="dialog" aria-label="Filter employees">
        {/* Header */}
        <div className="eep-filter-header">
          <div className="eep-filter-header-top">
            <span className="eep-filter-title">Filter Employees</span>
            <button className="eep-filter-close-btn" onClick={onClose} aria-label="Close">
              <XIcon size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="eep-filter-body">
          {loadingMeta ? (
            <div className="eep-filter-loading">
              <div className="eep-spinner" />
              <span>Loading filters…</span>
            </div>
          ) : (
            <>
              {/* Department dropdown */}
              <div className="eep-filter-field">
                <label className="eep-filter-field-label">Department</label>
                <div className="eep-filter-select-wrap">
                  <select
                    className="eep-filter-select"
                    value={draftDeptId}
                    onChange={(e) => onChangeDept(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {depts?.map((d) => (
                      <option key={d?.id} value={d?.id}>
                        {d?.name}
                      </option>
                    ))}
                  </select>
                  <span className="eep-filter-select-arrow">▾</span>
                </div>
              </div>

              {/* Designation dropdown */}
              <div className="eep-filter-field">
                <label className="eep-filter-field-label">Designation</label>
                <div className="eep-filter-select-wrap">
                  <select
                    className="eep-filter-select"
                    value={draftDesigId}
                    onChange={(e) => onChangeDesig(e.target.value)}
                  >
                    <option value="">All Designations</option>
                    {desigs?.map((d) => (
                      <option key={d?.id} value={d?.id}>
                        {d?.name}
                      </option>
                    ))}
                  </select>
                  <span className="eep-filter-select-arrow">▾</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="eep-filter-footer">
          <button className="eep-filter-clear-btn" onClick={onClear}>
            Clear All
          </button>
          <button className="eep-filter-apply-btn" onClick={onApply}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Unenrolled employee list ──────────────────────────────── */
function UnenrolledList({ onSelect, orgId, locId, onNavigateBack }) {
  /* List state */
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  /* Search */
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* Filters */
  const [filterVisible, setFilterVisible] = useState(false);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [draftDeptId, setDraftDeptId] = useState("");
  const [draftDesigId, setDraftDesigId] = useState("");
  const [appliedDeptId, setAppliedDeptId] = useState("");
  const [appliedDesigId, setAppliedDesigId] = useState("");

  /* Stale request prevention */
  const reqIdRef = useRef(0);
  const listRef = useRef(null);

  /* ── Debounce search ── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchText]);

  /* ── Fetch ── */
  const fetchList = useCallback(
    async (pageNum, mode = "init") => {
      const reqId = ++reqIdRef.current;
      if (mode === "init") {
        setLoading(true);
        setEmployees([]);
      }
      if (mode === "refresh") {
        setRefreshing(true);
      }
      if (mode === "more") {
        setLoadingMore(true);
      }

      try {
        const res = await listUnenrolledEmployees({
          page: pageNum,
          size: PAGE_SIZE,
          organizationIds: orgId ? [orgId] : [],
          locationIds: locId ? [locId] : [],
          departmentIds: appliedDeptId ? [appliedDeptId] : [],
          designationIds: appliedDesigId ? [appliedDesigId] : [],
          searchInput: debouncedSearch.trim(),
          joinedFrom: "",
          joinedTo: "",
        });

        if (reqId !== reqIdRef.current) return; // stale — discard

        /* Response: { es, data: { pageNumber, totalPageNumber, totalResults, data: [...] } } */
        const d = res?.data?.data || res?.data || {};
        const results = Array.isArray(d.data) ? d.data : [];
        const total = d.totalResults ?? results?.length;
        const pages = d.totalPageNumber ?? (Math.ceil(total / PAGE_SIZE) || 1);

        setTotalCount(total);
        setPage(pageNum);
        setHasMore(pageNum + 1 < pages);

        if (mode === "more") {
          setEmployees((prev) => {
            const ids = new Set(prev?.map((e) => e?.employeeId));
            return [...prev, ...results.filter((e) => !ids.has(e?.employeeId))];
          });
        } else {
          setEmployees(results);
        }
      } catch (_) {
        if (reqId !== reqIdRef.current) return;
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
          setLoadingMore(false);
        }
      }
    },
    [orgId, locId, debouncedSearch, appliedDeptId, appliedDesigId]
  );

  /* Reset + refetch on search/filter change */
  useEffect(() => {
    fetchList(0, "init");
  }, [fetchList]);

  /* ── Scroll-based load-more ── */
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading || refreshing || loadingMore || !hasMore) return;
    const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
    if (pct >= 0.65) fetchList(page + 1, "more");
  }, [loading, refreshing, loadingMore, hasMore, page, fetchList]);

  /* ── Filter metadata ── */
  const loadFilterMeta = useCallback(async () => {
    if (depts?.length || desigs?.length || loadingMeta) return;
    setLoadingMeta(true);
    try {
      const [dRes, deRes] = await Promise.all([
        listDepartmentsForFilter(orgId),
        listDesignationsForFilter(orgId),
      ]);
      /*
       * Full response shape (axios wraps once):
       *   res.data = { status, data: { es, message, statusCode,
       *     data: { pageNumber, totalResults, data: [ {departmentId, departmentName, ...} ] }
       *   }}
       * So the array lives at: res.data.data.data.data
       */
      const dData = dRes?.data?.data?.data?.data;
      const deData = deRes?.data?.data?.data?.data;
      setDepts(
        Array.isArray(dData)
          ? dData.map((i) => ({ id: i?.departmentId, name: i?.departmentName }))
          : []
      );
      setDesigs(
        Array.isArray(deData)
          ? deData.map((i) => ({ id: i?.designationId, name: i?.designationName }))
          : []
      );
    } catch (_) {
      /* filter stays usable on error */
    } finally {
      setLoadingMeta(false);
    }
  }, [orgId, depts?.length, desigs?.length, loadingMeta]);

  const openFilter = () => {
    setDraftDeptId(appliedDeptId);
    setDraftDesigId(appliedDesigId);
    setFilterVisible(true);
    loadFilterMeta();
  };
  const applyFilters = () => {
    setAppliedDeptId(draftDeptId);
    setAppliedDesigId(draftDesigId);
    setFilterVisible(false);
  };
  const clearDraft = () => {
    setDraftDeptId("");
    setDraftDesigId("");
  };
  const activeFilterCount = (appliedDeptId ? 1 : 0) + (appliedDesigId ? 1 : 0);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (_) {
      return iso.slice(0, 10);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="eep-header">
        <button className="eep-back-btn" onClick={onNavigateBack} aria-label="Back">
          <BackIcon />
        </button>
        <div className="eep-header-text">
          <h1>
            Unenrolled Employees
            {totalCount > 0 && <span className="eep-count-badge">{totalCount}</span>}
          </h1>
          <p>Select an employee to register their face</p>
        </div>
        <button
          className="eep-refresh-btn"
          onClick={() => fetchList(0, "refresh")}
          title="Refresh list"
          disabled={refreshing || loading}
        >
          <RefreshIcon />
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="eep-search-bar">
        <div className="eep-search-input-wrap">
          <SearchIcon />
          <input
            type="text"
            className="eep-search-input"
            placeholder="Search by name or ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              className="eep-search-clear"
              onClick={() => setSearchText("")}
              aria-label="Clear search"
            >
              <XIcon size={14} />
            </button>
          )}
        </div>
        <button className="eep-filter-btn" onClick={openFilter} aria-label="Filters">
          <FilterIcon />
          Filters
          {activeFilterCount > 0 && <span className="eep-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Employee list */}
      <div className="eep-list-wrap" ref={listRef} onScroll={handleScroll}>
        {loading ? (
          <div className="eep-list-center">
            <div className="eep-spinner" />
            <p>Loading employees…</p>
          </div>
        ) : employees?.length === 0 ? (
          <div className="eep-list-center">
            <div className="eep-empty-icon">
              <PersonIcon />
            </div>
            <p className="eep-empty-title">All employees are enrolled!</p>
            <p className="eep-empty-sub">No unenrolled employees match your search or filters.</p>
          </div>
        ) : (
          <>
            {employees?.map((emp) => {
              const name =
                emp?.fullName ||
                [emp?.firstName, emp?.lastName].filter(Boolean).join(" ") ||
                emp?.name ||
                "—";
              const displayId = emp?.employeeDisplayId || emp?.employeeId || "—";
              const joined = emp?.dateOfJoining ? `Joined: ${formatDate(emp?.dateOfJoining)}` : "";
              return (
                <button
                  key={emp?.employeeId}
                  className="eep-emp-card"
                  onClick={() => onSelect(emp)}
                  aria-label={`Enroll ${name}`}
                >
                  <div className="eep-emp-card-avatar">
                    <PersonIcon />
                  </div>
                  <div className="eep-emp-card-info">
                    <span className="eep-emp-card-name">{name}</span>
                    <span className="eep-emp-card-id">{displayId}</span>
                    {joined && <span className="eep-emp-card-date">{joined}</span>}
                  </div>
                  <ChevronRight />
                </button>
              );
            })}

            {/* Load more */}
            {loadingMore && (
              <div className="eep-load-more">
                <div className="eep-spinner-sm" /> Loading more…
              </div>
            )}
            {!hasMore && employees?.length > 0 && (
              <p className="eep-list-end">
                All {totalCount} employee{totalCount !== 1 ? "s" : ""} shown
              </p>
            )}
          </>
        )}
      </div>

      {/* Filter sidebar */}
      <FilterSidebar
        visible={filterVisible}
        depts={depts}
        desigs={desigs}
        loadingMeta={loadingMeta}
        draftDeptId={draftDeptId}
        draftDesigId={draftDesigId}
        onChangeDept={setDraftDeptId}
        onChangeDesig={setDraftDesigId}
        onApply={applyFilters}
        onClear={clearDraft}
        onClose={() => setFilterVisible(false)}
      />
    </>
  );
}

/* ─── Registration flow ─────────────────────────────────────── */
function RegisterEmployee({ employee, orgId, locId, onBack }) {
  /*
   * step: "input" → "camera" → "preview" → "enrolling" → "success"
   */
  const [step, setStep] = useState("input");
  const [employeeId, setEmployeeId] = useState(
    employee?.employeeDisplayId || employee?.employeeId || ""
  );
  const [checkLoading, setCheckLoading] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null); // null | true | false
  const [showAlreadyEnrolled, setShowAlreadyEnrolled] = useState(false);

  /* Camera */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraFacing, setCameraFacing] = useState("user");

  /* Capture / quality */
  const [capturing, setCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState(""); // guidance message
  const [captureStatusOk, setCaptureStatusOk] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null); // { dataUrl, brightness, sizeOk }

  /* Submission */
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitOk, setSubmitOk] = useState(true);
  const [apiMessage, setApiMessage] = useState("");

  /* ── Camera lifecycle ── */
  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    setCameraReady(false);
    setCameraError(null);

    (async () => {
      try {
        // Stop any existing stream first
        streamRef.current?.getTracks().forEach((t) => t.stop());

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: cameraFacing,
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (!cancelled) {
              videoRef.current?.play();
              setCameraReady(true);
            }
          };
        }
      } catch (err) {
        if (!cancelled) {
          setCameraError(
            err?.name === "NotAllowedError"
              ? "Camera access denied. Please allow camera permissions and try again."
              : "Could not access camera. Please check your device settings."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraReady(false);
    };
  }, [step, cameraFacing]);

  /* Stop stream when leaving camera/preview */
  useEffect(() => {
    if (step !== "camera" && step !== "preview") {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [step]);

  /* ── Step 1: Check enrollment status ── */
  const handleProceed = useCallback(async () => {
    const id = employeeId.trim();
    if (!id) return;
    setCheckLoading(true);
    try {
      const res = await checkEnrollmentStatusById(id, orgId, locId);
      const data = res?.data?.data ?? res?.data;
      // Response: { success, message, data: boolean }
      const isEnrolled = data?.data === true || data === true;
      setEnrollmentStatus(isEnrolled);
      if (isEnrolled) {
        setShowAlreadyEnrolled(true);
      } else {
        setStep("camera");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Not found → treat as not enrolled
        setEnrollmentStatus(false);
        setStep("camera");
      } else {
        setCaptureStatus(
          err?.response?.data?.message ||
            err?.response?.data?.data?.message ||
            "Could not check enrollment status. Please try again."
        );
        setCaptureStatusOk(false);
      }
    } finally {
      setCheckLoading(false);
    }
  }, [employeeId, orgId, locId]);

  /* ── Single frame capture ── */
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const brightness = estimateBrightness(canvas);
    const b64 = dataUrl.split(",")[1] || "";
    return { dataUrl, brightness, sizeOk: b64.length >= MIN_B64_LEN };
  }, []);

  /* ── Burst capture ── */
  const handleCapture = useCallback(async () => {
    if (capturing || !cameraReady) return;
    setCapturing(true);
    setCaptureStatus("Capturing photo…");
    setCaptureStatusOk(true);

    const frames = [];
    await new Promise((resolve) => {
      let count = 0;
      const tick = () => {
        const f = captureFrame();
        if (f) frames.push(f);
        count++;
        if (count >= BURST_COUNT) {
          resolve();
          return;
        }
        setTimeout(tick, BURST_INTERVAL_MS);
      };
      tick();
    });

    setCapturing(false);

    if (!frames.length) {
      setCaptureStatus("Could not capture a frame. Please try again.");
      setCaptureStatusOk(false);
      return;
    }

    // Pick best: brightness-passing + largest first
    const passing = frames?.filter(
      (f) => f?.brightness >= BRIGHTNESS_MIN && f?.brightness <= BRIGHTNESS_MAX && f?.sizeOk
    );
    const best = passing?.length
      ? passing?.reduce((a, b) => (b?.dataUrl?.length > a?.dataUrl?.length ? b : a))
      : frames?.reduce((a, b) => (b?.dataUrl?.length > a?.dataUrl?.length ? b : a));

    // Quality warning even if we proceed
    let qualityError = null;
    if (best?.brightness < BRIGHTNESS_MIN) {
      qualityError = "Too dark — move to a well-lit area.";
    } else if (best?.brightness > BRIGHTNESS_MAX) {
      qualityError = "Too bright — avoid direct light or backlight.";
    } else if (!best?.sizeOk) {
      qualityError = "Image too small — move closer to the camera.";
    }

    if (qualityError && !passing.length) {
      setCaptureStatus(qualityError);
      setCaptureStatusOk(false);
      return; // don't proceed to preview
    }

    setCapturedImage(best);
    setCaptureStatus("");
    setStep("preview");
  }, [capturing, cameraReady, captureFrame]);

  /* ── Submit enrollment ── */
  const handleRegister = useCallback(async () => {
    if (!capturedImage?.dataUrl) return;
    setStep("enrolling");
    setSubmitting(true);
    setSubmitMsg("Enrolling face…");

    const payload = {
      employeeId: employeeId.trim(),
      imageBase64: capturedImage.dataUrl,
      organizationId: orgId,
      locationId: locId || null,
      enrolledBy: "HRMS_ADMIN",
      createdBy: "HRMS_ADMIN",
      updatedBy: "HRMS_ADMIN",
      createdByName: "HR Admin",
      updatedByName: "HR Admin",
      challengeToken: null,
    };

    try {
      const fn = enrollmentStatus === true ? enrollFaceAdditionalEnroll : enrollFaceEnroll;
      const res = await fn(payload);
      const d = res?.data?.data ?? res?.data ?? {};
      const ok =
        d?.success === true ||
        d?.status === "SUCCESS" ||
        d?.es === 0 ||
        res?.data?.success === true ||
        res?.data?.es === 0;
      if (ok) {
        setApiMessage(d?.message || res?.data?.message || "Enrollment successful!");
        setSubmitOk(true);
        setStep("success");
      } else {
        setSubmitMsg(d?.message || res?.data?.message || "Enrollment failed. Please try again.");
        setSubmitOk(false);
        setStep("preview");
      }
    } catch (err) {
      setSubmitMsg(
        err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          "Network error — check your connection and try again."
      );
      setSubmitOk(false);
      setStep("preview");
    } finally {
      setSubmitting(false);
    }
  }, [capturedImage, employeeId, orgId, locId, enrollmentStatus]);

  /* ── Reset for "Register Another" ── */
  const handleRegisterAnother = () => {
    setCapturedImage(null);
    setEnrollmentStatus(null);
    setCaptureStatus("");
    setSubmitMsg("");
    setApiMessage("");
    setStep("input");
  };

  const empName =
    [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") || employee?.name || "";
  const empDisplayId = employee?.employeeDisplayId || employee?.employeeId || "";

  /* ── Render ── */
  return (
    <>
      {/* Header */}
      <div className="eep-header">
        <button
          className="eep-back-btn"
          onClick={() => {
            // Stop camera if running
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            onBack();
          }}
          aria-label="Back to employee list"
        >
          <BackIcon />
        </button>
        <div className="eep-header-text">
          <h1>{step === "success" ? "Enrolled Successfully" : "Register Employee"}</h1>
          <p>
            {step === "input" && "Confirm employee ID before capture"}
            {step === "camera" && "Position face within the oval guide"}
            {step === "preview" && "Review the captured photo before submitting"}
            {step === "enrolling" && "Sending face data to server…"}
            {step === "success" && `${empName || employeeId} has been registered`}
          </p>
        </div>
      </div>

      <div className="eep-reg-body">
        <div className="eep-reg-card">
          {/* Employee info strip (always visible once selected) */}
          {(empName || empDisplayId) && step !== "success" && (
            <div className="eep-emp-banner">
              <div className="eep-emp-info">
                <span className="eep-emp-name">{empName || employeeId}</span>
                {empDisplayId && empDisplayId !== employeeId && (
                  <span className="eep-emp-id">{empDisplayId}</span>
                )}
              </div>
            </div>
          )}

          {/* ─── Step: input ─── */}
          {step === "input" && (
            <div className="eep-step-input">
              <label className="eep-field-label">Employee ID</label>
              <input
                type="text"
                className="eep-field-input"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="e.g. EMP0000123"
                autoComplete="off"
                onKeyDown={(e) => e.key === "Enter" && !checkLoading && handleProceed()}
              />
              {captureStatus && !captureStatusOk && (
                <div className="eep-step-error">{captureStatus}</div>
              )}
              <button
                className="eep-action-btn"
                onClick={handleProceed}
                disabled={!employeeId.trim() || checkLoading}
              >
                {checkLoading ? (
                  <>
                    <div className="eep-spinner-sm" /> Checking enrollment…
                  </>
                ) : (
                  <>
                    <CameraIcon /> Proceed to Capture
                  </>
                )}
              </button>
            </div>
          )}

          {/* ─── Step: camera ─── */}
          {step === "camera" && (
            <div className="eep-step-camera">
              {/* Camera view */}
              <div className="eep-video-wrap">
                {cameraError ? (
                  <div className="eep-camera-error">
                    <CameraIcon />
                    <p>{cameraError}</p>
                    <button
                      className="eep-retry-btn"
                      onClick={() => {
                        setCameraError(null);
                        setCameraFacing("user");
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`eep-video${cameraReady ? " ready" : ""}`}
                    />
                    {!cameraReady && (
                      <div className="eep-video-loading">
                        <div className="eep-spinner" />
                        <p>Starting camera…</p>
                      </div>
                    )}
                    {cameraReady && <div className="eep-face-guide" aria-hidden="true" />}
                    {/* Flip camera */}
                    <button
                      className="eep-cam-flip-btn"
                      onClick={() =>
                        setCameraFacing((f) => (f === "user" ? "environment" : "user"))
                      }
                      aria-label="Flip camera"
                      title="Flip camera"
                    >
                      <RefreshIcon />
                    </button>
                  </>
                )}
              </div>
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* Quality status */}
              {captureStatus && (
                <div className={`eep-capture-status ${captureStatusOk ? "ok" : "fail"}`}>
                  {captureStatus}
                </div>
              )}
              {!captureStatus && cameraReady && (
                <div className="eep-capture-hint">
                  Position face within the oval guide. Ensure even lighting.
                </div>
              )}

              {/* Capture button */}
              <button
                className="eep-action-btn"
                onClick={handleCapture}
                disabled={!cameraReady || capturing}
              >
                {capturing ? (
                  <>
                    <div className="eep-spinner-sm" /> Capturing…
                  </>
                ) : (
                  <>
                    <CameraIcon /> Capture Photo
                  </>
                )}
              </button>

              <button className="eep-text-btn" onClick={() => setStep("input")}>
                ← Back to input
              </button>
            </div>
          )}

          {/* ─── Step: preview ─── */}
          {step === "preview" && (
            <div className="eep-step-preview">
              <p className="eep-preview-label">Confirm Photo</p>
              <div className="eep-preview-img-wrap">
                {capturedImage?.dataUrl && (
                  <img
                    src={capturedImage.dataUrl}
                    alt="Captured face"
                    className="eep-preview-img"
                  />
                )}
              </div>

              {/* Submission error (if came back from enrolling) */}
              {submitMsg && !submitOk && <div className="eep-step-error">{submitMsg}</div>}

              <div className="eep-preview-actions">
                <button
                  className="eep-secondary-btn"
                  onClick={() => {
                    setCapturedImage(null);
                    setSubmitMsg("");
                    setStep("camera");
                  }}
                >
                  Retake
                </button>
                <button className="eep-action-btn" onClick={handleRegister}>
                  Register
                </button>
              </div>
            </div>
          )}

          {/* ─── Step: enrolling ─── */}
          {step === "enrolling" && (
            <div className="eep-step-enrolling">
              <div className="eep-enrolling-spinner" />
              <p className="eep-enrolling-msg">{submitMsg || "Enrolling face…"}</p>
            </div>
          )}

          {/* ─── Step: success ─── */}
          {step === "success" && (
            <div className="eep-step-success">
              <div className="eep-success-icon">
                <CheckCircleIcon />
              </div>
              <h2 className="eep-success-title">Enrolled Successfully</h2>
              <p className="eep-success-sub">
                Employee <strong>{employeeId}</strong> face has been registered.
              </p>
              {apiMessage && <p className="eep-success-api-msg">{apiMessage}</p>}
              <div className="eep-success-actions">
                <button className="eep-secondary-btn" onClick={handleRegisterAnother}>
                  Register Another
                </button>
                <button className="eep-action-btn" onClick={onBack}>
                  Back to List
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Already Enrolled modal */}
      {showAlreadyEnrolled && (
        <div className="eep-modal-backdrop" onClick={() => setShowAlreadyEnrolled(false)}>
          <div className="eep-modal" onClick={(e) => e.stopPropagation()}>
            <div className="eep-modal-icon">⚠</div>
            <h3 className="eep-modal-title">Already Enrolled</h3>
            <p className="eep-modal-msg">
              Employee <strong>{employeeId}</strong> already has a face enrolled. Do you want to add
              another photo?
            </p>
            <div className="eep-modal-actions">
              <button
                className="eep-secondary-btn"
                onClick={() => {
                  setShowAlreadyEnrolled(false);
                  setStep("input");
                }}
              >
                Cancel
              </button>
              <button
                className="eep-action-btn"
                onClick={() => {
                  setShowAlreadyEnrolled(false);
                  setStep("camera");
                }}
              >
                Add Photo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Main orchestrator ─────────────────────────────────────── */
function EmployeeEnrollmentContent() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(UserContext) || {};

  const orgId = localStorage.getItem("orgId") || "";
  const locId = localStorage.getItem("locId") || "";

  /* Role guard */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("user_roles") || "[]");
    if (!roles.includes("HRMS_ADMIN")) {
      navigate("/products/staff-management", { replace: true });
    }
  }, [navigate]);

  const [view, setView] = useState("list"); // "list" | "register"
  const [selectedEmployee, setSelected] = useState(null);

  const handleSelect = (emp) => {
    setSelected(emp);
    setView("register");
  };
  const handleBack = () => {
    setSelected(null);
    setView("list");
  };

  return (
    <div className={`eep-root${isDarkMode ? " dark" : ""}`}>
      {view === "list" ? (
        <UnenrolledList
          onSelect={handleSelect}
          orgId={orgId}
          locId={locId}
          onNavigateBack={() => navigate("/products/staff-management")}
        />
      ) : (
        <RegisterEmployee
          employee={selectedEmployee}
          orgId={orgId}
          locId={locId}
          onBack={handleBack}
        />
      )}
    </div>
  );
}

/* ─── Page export (add-on gate) ─────────────────────────────── */
export default function EmployeeEnrollmentPage() {
  return (
    <AddonGate addonName="pallet workforce">
      <EmployeeEnrollmentContent />
    </AddonGate>
  );
}
